import {
	type GetShipmentsParams as FilterParams,
	GetShipmentsFilterStatusesItem as ShipmentStatus,
} from '@uturn/api/v3';
import {
	DEFAULT_SHIPMENT_TAB,
	DEFAULT_STATUS_FILTERS,
	DEFAULT_STATUS_OPTIONS,
	type DefaultFilters,
	getDefaultStatusOptions,
	getShipmentsStatusTabs,
	type ShipmentsStatusTab,
	ShipmentsStatusTabRaw,
	type StatusOption,
	type StatusTab,
} from '@uturn/portal/types/shipments';
import { type TFunction } from 'i18next';
import { create } from 'zustand';

type FilterParamsPartial = Partial<FilterParams>;
type TFunc = TFunction<'translation', undefined, 'translation'>;

// TODO: See also: FilterContext in apps/portal/src/modules/shipments/components/filters/context.tsx.
// TODO: See also: useFilterParams in apps/portal/src/modules/shipments/hooks/use-filter-params.ts.
// TODO: See also: SHIPMENTS_STATUS_TABS in apps/portal/src/types/shipments.ts
// TODO: See also: ShipmentsSearchSchema in apps/portal/src/router/schema/shipments-search.ts.
// TODO: Decommission filtering states after tanstack-router fulling replaces them.
// TODO: Decommission pagination states after tanstack-router fulling replaces them.
type ShipmentsSearchStore = {
	totalShipments: number;
	pageSize: number;
	totalPages: number;
	setPagination: (total: number | undefined, size: number | undefined) => void;
	tabs: ShipmentsStatusTab[];
	activeTab: ShipmentsStatusTab;
	activeStatusTab: StatusTab;
	defaultFilters: DefaultFilters;
	statuses: ShipmentStatus[];
	statusOptions: StatusOption[];
	initTabs: (
		t: TFunc,
		pages: ShipmentsStatusTabRaw[],
		pathname: string,
		groupId?: string,
	) => void;
	shipmentsSearchFilters: FilterParamsPartial;
	setShipmentsSearchFilters: (filters: FilterParamsPartial) => void;
};

export const useShipmentsSearchStore = create<ShipmentsSearchStore>(
	(set, get) => ({
		totalShipments: 0,
		pageSize: 0,
		totalPages: 0,
		setPagination: (total, size) => {
			if (total === undefined || size === undefined) {
				return;
			}

			// Prevent division by zero
			if (size === 0) {
				set({ totalShipments: 0, pageSize: 0, totalPages: 0 });
				return;
			}

			const totalShipments = total;
			const pageSize = size;
			const totalPages = Math.ceil(totalShipments / pageSize);

			set({ totalShipments, pageSize, totalPages });
		},
		tabs: [DEFAULT_SHIPMENT_TAB],
		activeTab: DEFAULT_SHIPMENT_TAB,
		activeStatusTab: DEFAULT_SHIPMENT_TAB.navTab,
		defaultFilters: DEFAULT_STATUS_FILTERS,
		statuses: DEFAULT_STATUS_FILTERS?.['filter.statuses'] ?? [],
		statusOptions: DEFAULT_STATUS_OPTIONS,
		initTabs: (t, pages, pathname, groupId) => {
			const {
				activeTab,
				activeStatusTab,
				defaultFilters,
				statuses,
				statusOptions,
			} = get();

			const pathToCheck =
				groupId && groupId !== ''
					? pathname.replace(groupId, '$groupId')
					: pathname;

			const newGroupFilters =
				// FIXME: "Exclude all groups" filter is broken.
				// TODO: Double check if 'filter.groups' excludes instead of includes!
				groupId && groupId !== '' ? { 'filter.groups': [Number(groupId)] } : {};

			let newActiveTab = activeTab;
			let newActiveStatusTab = activeStatusTab;
			let newDefaultFilters = defaultFilters;
			let newStatuses = statuses;
			let newStatusOptions = statusOptions;

			const tabs = getShipmentsStatusTabs(t, pages).map((page) => {
				const active = pathToCheck === page.to;
				const tab: ShipmentsStatusTab = { ...page, active };
				if (active && activeStatusTab !== tab.navTab) {
					newActiveTab = tab;
					newActiveStatusTab = tab.navTab;
					newDefaultFilters = {
						...tab.defaultFilters,
						...newGroupFilters,
					};
					newStatuses = newDefaultFilters['filter.statuses'] ?? [];
					newStatusOptions = getDefaultStatusOptions(t, newActiveStatusTab);
				}
				return tab;
			});

			set({
				tabs,
				activeTab: newActiveTab,
				activeStatusTab: newActiveStatusTab,
				statuses: newStatuses,
				statusOptions: newStatusOptions,
				defaultFilters: newDefaultFilters,
				shipmentsSearchFilters: newDefaultFilters,
			});
		},
		shipmentsSearchFilters: {},
		setShipmentsSearchFilters: (filters) => {
			const { defaultFilters } = get();

			const shipmentsSearchFilters = {
				...defaultFilters,
				...filters,
			};

			const noStatusSelected =
				Array.isArray(shipmentsSearchFilters['filter.statuses']) &&
				shipmentsSearchFilters['filter.statuses'].length === 0;

			set(
				noStatusSelected
					? {
							shipmentsSearchFilters,
							totalShipments: 0,
							pageSize: 0,
							totalPages: 0,
						}
					: { shipmentsSearchFilters },
			);
		},
	}),
);
